// The line where the footer starts, as something to take hold of.
//
// Dragging it down gives the page more room before the footer begins and
// dragging it up brings the footer closer. The value is the same one the
// settings slider writes, so the two agree, and it is stored per browser.

import { useEffect, useRef, useState } from 'react';
import { applySettings, readSettings, writeSettings } from '../theme.ts';

export function FooterHandle() {
  const [height, setHeight] = useState(() => readSettings().contentHeight);
  const dragging = useRef(false);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (!dragging.current) return;
      e.preventDefault();
      const next = Math.round(Math.min(200, Math.max(60, (e.clientY / window.innerHeight) * 100)));
      setHeight(next);
      document.documentElement.style.setProperty('--content-min', `${next}dvh`);
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.classList.remove('dragging-footer');
      const settings = { ...readSettings(), contentHeight: height };
      writeSettings(settings);
      applySettings(settings);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [height]);

  const nudge = (by: number) => {
    const next = Math.min(200, Math.max(60, height + by));
    setHeight(next);
    const settings = { ...readSettings(), contentHeight: next };
    writeSettings(settings);
    applySettings(settings);
  };

  return (
    <div
      className="footerhandle"
      role="separator"
      aria-label="Where the footer starts"
      aria-valuenow={height}
      aria-valuemin={60}
      aria-valuemax={200}
      tabIndex={0}
      onPointerDown={(e) => {
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        dragging.current = true;
        document.body.classList.add('dragging-footer');
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') nudge(5);
        else if (e.key === 'ArrowUp') nudge(-5);
        else return;
        e.preventDefault();
      }}
      title="Drag to move where the footer starts"
    >
      <span aria-hidden="true" />
    </div>
  );
}
